/**
 * Wage Reports — admin list page.
 * Lists every Owner Wage Report prepared for a client in this firm.
 * Click "New report" → pick a client → opens the builder.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Plus, FileText, Trash2, Lock, Search, LayoutTemplate } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import ReportTemplatesDialog from "./ReportTemplatesDialog";

const STATUS_META = {
    draft: { label: "Draft", chip: "bg-amber-50 text-amber-800 border-amber-200" },
    sent: { label: "Sent", chip: "bg-emerald-50 text-emerald-800 border-emerald-200" },
};

export default function WageReports() {
    const nav = useNavigate();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [filter, setFilter] = useState("all");

    const [picking, setPicking] = useState(false);
    const [templatesOpen, setTemplatesOpen] = useState(false);
    const [templates, setTemplates] = useState([]);
    const [selectedTpl, setSelectedTpl] = useState("");
    const [clients, setClients] = useState([]);
    const [clientQ, setClientQ] = useState("");
    const [clientsLoading, setClientsLoading] = useState(false);
    const [creating, setCreating] = useState(false);

    async function load() {
        setLoading(true);
        try {
            const { data } = await api.get("/wage-reports");
            setRows(data);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    }
    useEffect(() => { load(); }, []);

    const loadTemplates = async () => {
        try {
            const { data } = await api.get("/wage-report-templates");
            setTemplates(data || []);
            const def = (data || []).find((t) => t.is_default);
            if (def) setSelectedTpl(def.id);
        } catch { /* non-fatal */ }
    };
    useEffect(() => { loadTemplates(); }, []);

    async function openPicker() {
        setPicking(true);
        if (clients.length) return;
        setClientsLoading(true);
        try {
            const { data } = await api.get("/clients");
            setClients(data || []);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setClientsLoading(false); }
    }

    const clientMatches = useMemo(() => {
        if (!clientQ) return clients.slice(0, 50);
        const needle = clientQ.toLowerCase();
        return clients.filter((c) => (c.name || "").toLowerCase().includes(needle) ||
            (c.contact_email || "").toLowerCase().includes(needle)).slice(0, 50);
    }, [clients, clientQ]);

    async function createFor(client) {
        setCreating(true);
        try {
            const body = { client_id: client.id };
            if (selectedTpl) body.template_id = selectedTpl;
            const { data } = await api.post("/wage-reports", body);
            toast.success(`Draft report started for ${client.name}`);
            nav(`/app/wage-reports/${data.id}`);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setCreating(false); }
    }

    const filtered = useMemo(() => {
        let out = rows;
        if (filter !== "all") out = out.filter((r) => r.status === filter);
        if (q) {
            const needle = q.toLowerCase();
            out = out.filter((r) => (r.client_name || "").toLowerCase().includes(needle) ||
                (r.client_email || "").toLowerCase().includes(needle));
        }
        return out;
    }, [rows, q, filter]);

    async function remove(id, name) {
        if (!window.confirm(`Delete draft report for ${name}? This can't be undone.`)) return;
        try {
            await api.delete(`/wage-reports/${id}`);
            toast.success("Deleted");
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
    }

    return (
        <div className="space-y-5" data-testid="wage-reports-page">
            <header className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <h1 className="text-xl font-semibold text-[#0F1115]">Wage Reports</h1>
                    <p className="text-[12.5px] text-[#5E636E]">Prepare a branded Owner Wage Report for any client, review it on screen, then email it to them with a one-click portal link + PDF.</p>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setTemplatesOpen(true)} className="h-9 text-[12.5px]" data-testid="wage-reports-templates">
                        <LayoutTemplate className="h-3.5 w-3.5 mr-1" /> Templates
                    </Button>
                    <Button onClick={openPicker} className="bg-[#0F1115] hover:bg-[#2E2E2E] text-white h-9 text-[12.5px]" data-testid="wage-reports-new">
                        <Plus className="h-3.5 w-3.5 mr-1" /> New report for client…
                    </Button>
                </div>
            </header>

            <div className="flex flex-wrap items-center gap-2 border-b border-[#E4E4E7] pb-2">
                {[
                    ["all", "All"],
                    ["draft", "Draft"],
                    ["sent", "Sent"],
                ].map(([k, label]) => (
                    <button key={k} onClick={() => setFilter(k)}
                        className={`text-[12.5px] px-2.5 py-1 rounded-full border ${filter === k ? "bg-[#0F1115] text-white border-[#0F1115]" : "border-[#E4E4E7] bg-white text-[#5E636E] hover:text-[#0F1115]"}`}
                        data-testid={`wage-reports-filter-${k}`}>
                        {label}
                    </button>
                ))}
                <div className="ml-auto w-72">
                    <Input placeholder="Search client name / email…" value={q} onChange={(e) => setQ(e.target.value)} className="h-8 text-[13px]" data-testid="wage-reports-search" />
                </div>
            </div>

            {loading ? (
                <div className="py-10 text-center text-[#5E636E]"><Loader2 className="inline h-4 w-4 animate-spin" /></div>
            ) : filtered.length === 0 ? (
                <div className="rounded-md border border-dashed border-[#E4E4E7] bg-white p-8 text-center text-[13px] text-[#5E636E]">
                    No reports yet. Click <b>New report for client…</b> to prepare the first one.
                </div>
            ) : (
                <div className="rounded-md border border-[#E4E4E7] bg-white overflow-x-auto">
                    <table className="w-full text-[13px]">
                        <thead className="text-[11px] uppercase tracking-wider text-[#5E636E]">
                            <tr>
                                <th className="text-left px-3 py-2">Client</th>
                                <th className="text-left px-3 py-2">Status</th>
                                <th className="text-left px-3 py-2">Prepared by</th>
                                <th className="text-right px-3 py-2">Recommended $/wk</th>
                                <th className="text-right px-3 py-2">Updated</th>
                                <th className="text-right px-3 py-2 w-56">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((r) => {
                                const sm = STATUS_META[r.status] || STATUS_META.draft;
                                const recWk = r.result?.recommended_weekly;
                                return (
                                    <tr key={r.id} className="border-t border-[#EEEDE7]" data-testid={`wage-reports-row-${r.id}`}>
                                        <td className="px-3 py-2">
                                            <div className="font-medium text-[#0F1115]">{r.client_name || "—"}</div>
                                            <div className="text-[11px] text-[#5E636E]">{r.client_email || "—"}</div>
                                        </td>
                                        <td className="px-3 py-2">
                                            <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10.5px] font-semibold ${sm.chip}`}>
                                                {r.status === "sent" && <Lock className="h-2.5 w-2.5" />}{sm.label}
                                                {r.version > 1 ? ` · v${r.version}` : ""}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2 text-[#5E636E]">{r.prepared_by_name || "—"}</td>
                                        <td className="px-3 py-2 text-right tabular-nums">{recWk ? `$${Number(recWk).toLocaleString("en-NZ", { maximumFractionDigits: 0 })}` : "—"}</td>
                                        <td className="px-3 py-2 text-right text-[11.5px] text-[#5E636E]">{r.updated_at ? new Date(r.updated_at).toLocaleDateString("en-NZ") : "—"}</td>
                                        <td className="px-3 py-2 text-right">
                                            <div className="inline-flex gap-1 justify-end">
                                                <Link to={`/app/wage-reports/${r.id}`} className="inline-flex items-center gap-1 rounded-md border border-[#E4E4E7] bg-white px-2 py-1 text-[11.5px] hover:bg-zinc-50" data-testid={`wage-reports-open-${r.id}`}>
                                                    <FileText className="h-3 w-3" /> Open
                                                </Link>
                                                {r.status === "draft" && (
                                                    <Button size="icon" variant="ghost" className="h-7 w-7 text-[#5E636E] hover:text-red-700" onClick={() => remove(r.id, r.client_name)} data-testid={`wage-reports-delete-${r.id}`}>
                                                        <Trash2 className="h-3.5 w-3.5" />
                                                    </Button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            <Dialog open={picking} onOpenChange={setPicking}>
                <DialogContent className="max-w-xl">
                    <DialogHeader>
                        <DialogTitle>Pick a client</DialogTitle>
                    </DialogHeader>
                    {templates.length > 0 && (
                        <div>
                            <label className="text-[10.5px] uppercase tracking-wider text-[#5E636E]">Apply template</label>
                            <select value={selectedTpl} onChange={(e) => setSelectedTpl(e.target.value)}
                                className="mt-1 w-full h-9 rounded-md border border-[#E4E4E7] bg-white px-3 text-[13px]"
                                data-testid="wage-reports-picker-template">
                                <option value="">— None —</option>
                                {templates.map((t) => (
                                    <option key={t.id} value={t.id}>{t.name}{t.is_default ? " · default" : ""}</option>
                                ))}
                            </select>
                        </div>
                    )}
                    <div className="relative">
                        <Search className="h-3.5 w-3.5 absolute left-3 top-2.5 text-[#5E636E]" />
                        <Input autoFocus placeholder="Search by name or email…" value={clientQ} onChange={(e) => setClientQ(e.target.value)} className="pl-8 h-9" data-testid="wage-reports-picker-search" />
                    </div>
                    <div className="max-h-80 overflow-y-auto rounded-md border border-[#E4E4E7] divide-y divide-[#EEEDE7]">
                        {clientsLoading ? (
                            <div className="py-6 text-center text-[#5E636E]"><Loader2 className="inline h-4 w-4 animate-spin" /></div>
                        ) : clientMatches.length === 0 ? (
                            <div className="py-6 text-center text-[13px] text-[#5E636E]">No clients match.</div>
                        ) : clientMatches.map((c) => (
                            <button key={c.id} onClick={() => createFor(c)} disabled={creating} className="w-full text-left px-3 py-2 hover:bg-zinc-50 disabled:opacity-50 flex items-center justify-between gap-3" data-testid={`wage-reports-picker-${c.id}`}>
                                <div className="min-w-0">
                                    <div className="font-medium text-[13px] text-[#0F1115] truncate">{c.name}</div>
                                    <div className="text-[11.5px] text-[#5E636E] truncate">{c.contact_email || "No email on file"}</div>
                                </div>
                                <Plus className="h-3.5 w-3.5 text-[#5E636E] shrink-0" />
                            </button>
                        ))}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setPicking(false)}>Cancel</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <ReportTemplatesDialog open={templatesOpen} onClose={() => setTemplatesOpen(false)} onChanged={loadTemplates} />
        </div>
    );
}
